import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { ChevronDownIcon } from '@cognite/cogs-icons-v11/system-icons/ChevronDownIcon.tsx';
import { useWidth } from '@cognite/cogs-utils-v11/helpers/viewport/use-width.tsx';

import { Tooltip } from '../tooltip';

const AccordionHeader = ({
  disabled,
  expand,
  title,
  subtitle,
  expanded,
  indicatorPlacement,
}: {
  disabled?: boolean;
  expand: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  expanded: boolean;
  indicatorPlacement: string;
}): React.ReactElement => {
  const subtitleRef = useRef<HTMLDivElement>(null);
  const [subtitleOverflowed, setSubtitleOverflowed] = useState(false);

  const subtitleWidth = useWidth(subtitleRef);

  useEffect(() => {
    const subtitlePlaceholder = subtitleRef.current;
    const pElement = subtitlePlaceholder?.getElementsByTagName('p')[0];
    if (pElement && pElement.scrollWidth > subtitlePlaceholder?.clientWidth) {
      setSubtitleOverflowed(true);
    } else {
      setSubtitleOverflowed(false);
    }
  }, [subtitleWidth]);

  const renderRightIndicator = () => {
    if (indicatorPlacement === 'left') return null;
    return (
      <ChevronDownIcon
        className={`cogs-v11 cogs-accordion__header__icon ${
          expanded ? 'cogs-v11 cogs-accordion__header__icon--expanded' : ''
        }`}
      />
    );
  };

  const renderLeftIndicator = () => {
    if (indicatorPlacement === 'right') return null;
    return (
      <ChevronDownIcon
        className={`cogs-v11 cogs-accordion__header__icon ${
          expanded ? 'cogs-v11 cogs-accordion__header__icon--expanded' : ''
        }`}
      />
    );
  };

  const handleExpand = () => {
    if (!disabled) {
      expand?.();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleExpand();
    }
  };

  return (
    <div
      className={classNames(
        'cogs-v11',
        'cogs-accordion__header',
        indicatorPlacement && `cogs-accordion__header--${indicatorPlacement}`
      )}
      aria-disabled={disabled}
      onClick={handleExpand}
      role="button"
      onKeyPress={handleKeyPress}
      tabIndex={0}
    >
      {renderLeftIndicator()}
      <div className="cogs-v11 cogs-accordion__header__title">
        <div
          ref={subtitleRef}
          className="cogs-v11 cogs-accordion__header__title--subtitle--placeholder"
        >
          <p>{subtitle}</p>
        </div>
        <Tooltip
          content={subtitle}
          disabled={!subtitleOverflowed || typeof subtitle !== 'string'}
          placement="top"
          interactive={false}
        >
          <>
            <p>{title}</p>
            {subtitle && <p className="subtitle">{subtitle}</p>}
          </>
        </Tooltip>
      </div>
      {renderRightIndicator()}
    </div>
  );
};

export default AccordionHeader;
